<script setup lang="ts">
// ================================
// Z3 — Arbitrage des dépassements (DEV-783 Lot B, décision #1).
// On présente TOUJOURS les deux scénarios côte à côte, « Couvrir les dépassements » (zéro
// dépassement) et « Assumer les dépassements », avec le risque chiffré de chaque côté ;
// le courtier tranche. Les noms viennent du dictionnaire client (DEV-2218). Le scénario retenu pilote le verdict (Z1) et la décomposition (Z5).
// Composant piloté par props (2 études) → testable en isolation.
// ================================
import { formateurFr } from "@modules/courtage/utils/optim-turpe-v2.nombre"
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import {
    cmdpsApplicable,
    estC5,
    getClasseDimensionnante,
    getCmdpsEstimeeEur,
    getEconomieFermeEur,
    getEconomieNetteEur,
    getUnitePuissance,
    PLAGE_ORDER,
} from "@modules/courtage/utils/optim-turpe-v2.helpers"
import {
    aucunDepassementEtabli,
    conseilDeRepli,
    SCENARIO_DE_LA_REGLE,
    lireArbitrageServi,
    conseilEtabli,
} from "@modules/courtage/utils/optim-turpe-v2.arbitrage"
import {
    LIBELLE_SCENARIO_ARBITRAGE,
    libelleClasseHoraire,
    libelleDureeHeures,
    libellePostes,
} from "@modules/courtage/utils/optim-turpe-v2.libelles"
import { enteteDetailScenarios } from "@modules/courtage/utils/optim-turpe-v2.entetes"
import { lireChiffre } from "@modules/courtage/utils/optim-turpe-v2.chiffre"
import { surLaPeriode } from "@modules/courtage/utils/optim-turpe-v2.titre"
import {
    classeMontant,
    formatNombreSigne,
    nombreEuros,
    nombreEurosDepassement,
    suiteColleeAuNombre,
} from "@modules/courtage/utils/optim-turpe-v2.montant"
import { NIVEAU_2 } from "@modules/courtage/utils/optim-turpe-v2.boutons"
import OptimTurpeV2Section from "./OptimTurpeV2Section.vue"
import type { BaseKpiPassThrough } from "@core/components/base/kpi/baseKpiPassThrough"
import type { OptimTurpeV2Etude } from "@modules/courtage/types/optim-turpe-v2"

type Scenario = "sans" | "avec"

const { t } = useI18n({
    locale: "fr",
    messages: {
        fr: {
            sans: {
                name: LIBELLE_SCENARIO_ARBITRAGE.sans,
                cmdps: "Aucun dépassement sur la période étudiée.",
            },
            avec: {
                name: LIBELLE_SCENARIO_ARBITRAGE.avec,
                // Q25 (14/10/2026) : la règle v2 recommande d'assumer quand
                // aucun dépassement n'est attendu ; la carte le dit.
                aucunDepassement: "Aucun dépassement sur la période étudiée.",
            },
            // S2 (14/10/2026) : toutes les puissances du scénario, poste par
            // poste avec leur code, ou « Tous les postes ».
            groupe: "{postes} : {avant} → {apres} {unite}",
            puissance: "Puissance souscrite {avant} → {apres} {unite}",
            retenir: "Retenir ce scénario",
            // Contrat v1.2 (D2) : la tuile affichée se lit à sa bordure, sans
            // étiquette « Affiché ». Revue 2 en production (I07, I35, I57) :
            // des étiquettes de deux mots au plus, la raison dessous.
            aucunDepassement: "aucun dépassement",
            // Q5, W50 : un seul marqueur, « Recommandé », avec sa raison.
            conseille: "Recommandé",
            optionChiffree: "Option chiffrée",
            raison: {
                "aucun-depassement": "aucun dépassement attendu",
                "couvrir-moins-cher": "assumer ne ferait rien gagner",
                "gain-couvre-le-risque":
                    "le gain vaut plus de deux fois les dépassements",
                pic_isole: "pic isolé",
                comportement_normal: "pointe régulière",
                saisonnier: "pointe saisonnière",
            },
            // Les montants portent la PÉRIODE analysée, jamais une année (A4),
            // dite avec son nombre de jours comme au bandeau et au temps 1
            // (finitions du 07/10/2026 : une seule unité sur la page).
            // Le chiffre principal de chaque carte est la NETTE (A3, DEV-2167).
            // Revue du 19/10/2026 (N3) : l'unité s'attache au nombre, sur la
            // même ligne, « −42 € HT net par an ».
            netteUnite: "€ HT net",
            fermeUnite: "€ HT",
            fermePrecision:
                "Part liée à la formule et aux puissances souscrites.",
            ecart: {
                egal: "Les deux scénarios reviennent au même sur la période.",
            },
            // Revue 2 en production (I57) : la réserve en UNE ligne ; l'écart
            // avec le contrat actuel se lit dans « Le détail des deux
            // scénarios ».
            cmdpsAvec:
                "Dépassements estimés : {duree}{value} € HT {periode}, compris dans le montant.",
            cmdpsAvecSansDuree:
                "Dépassements estimés : {value} € HT {periode}, compris dans le montant.",
            cmdpsNonServie:
                "Cette étude ne chiffre pas les dépassements de puissance.",
            fermeAbsente: "—",
            // DEV-2217 : le motif COMPOSÉ par le moteur passe avant tout. En
            // repli, la page dit la règle v2 du 12/10/2026 qui a décidé, avec
            // les mots du moteur ; la forme du pic, sans chiffre ni promesse,
            // n'est dite que lorsqu'elle décide (« évite des dépassements
            // fréquents » était servi pour 7 h sur l'année).
            // Règle v2 du 12/10/2026, dans son ordre et sans « nous » (Q25,
            // W51, W52, W57) ; la règle du triplement se lit dans l'aide.
            conseil: {
                aucunDepassement:
                    "Assumer les dépassements est recommandé : les puissances les plus basses n'auraient été dépassées à aucun moment sur la période étudiée.",
                couvrirMoinsCher:
                    "Couvrir les dépassements est recommandé : assumer les dépassements ne ferait rien gagner sur la période.",
                gainCouvreLeRisque:
                    "Assumer les dépassements est recommandé : le gain ({gain} € HT sur la période) vaut plus de deux fois les dépassements estimés ({depassements} € HT).",
                gainCouvreLeRisqueSansMontant:
                    "Assumer les dépassements est recommandé : le gain sur la période vaut plus de deux fois les dépassements estimés.",
                pic: "Les puissances souscrites sont tirées par un pic isolé en {plage} : mieux vaut assumer ce dépassement ponctuel que souscrire toute l'année pour le couvrir.",
                normal: "La puissance maximale du poste {plage} est atteinte souvent : mieux vaut la couvrir.",
                saisonnier:
                    "La pointe est saisonnière mais revient chaque année, en {plage} : mieux vaut la couvrir.",
            },
            // Revue du 08/10 (R1) : l'écart n'a pas de sens pour le client, il
            // s'écrit sans signe et porte ses mots.
            ecartCourt:
                "Écart de {ecart} € HT {periode} entre les deux scénarios.",
            footnote:
                "L'estimation des dépassements repose sur les relevés passés et ne présage pas des mois à venir.",
            detail: {
                // N4 : l'unité dite dans l'entête de chaque colonne.
                scenario: "Scénario",
                ferme: "Part liée à la formule et aux puissances souscrites (€ HT {periode})",
                depassements:
                    "Part liée aux dépassements, estimée (€ HT {periode})",
                nette: "Net (€ HT net {periode})",
            },
        },
    },
})

type Props = {
    etudeSans: OptimTurpeV2Etude | null
    etudeAvec: OptimTurpeV2Etude | null
    retenu: Scenario
    /** « Le détail des deux scénarios », replié par défaut, tenu par la page. */
    detailOuvert?: boolean
}
const props = withDefaults(defineProps<Props>(), { detailOuvert: false })
const emit = defineEmits<{
    select: [scenario: Scenario]
    detail: [ouvert: boolean]
}>()

const numberFr = formateurFr({ maximumFractionDigits: 0 })

type Card = {
    /** Le chiffre principal : la nette de la période, avec son signe. */
    principal: string
    /** L'unité attachée au chiffre, sur la même ligne (N3). */
    suffixe: string
    /** Ce que le chiffre est, quand ce n'est pas la nette ; `null` sinon. */
    precision: string | null
    /**
     * Le montant du chiffre principal, pour sa couleur : vert s'il fait
     * économiser, rouge s'il coûte plus, neutre à 0 (revue du 08/10, R1).
     */
    principalEur: number | null
    ferme: string
    /** `null` = montant non servi : on le DIT, on n'écrit pas 0 (revue C4). */
    cmdps: number | null
    structure: string
    /** Les montants servis, pour « Le détail des deux scénarios ». */
    fermeEur: number | null
    depassementsEur: number | null
    netteEur: number | null
    /** Le nombre de jours de la période analysée, ou `null`. */
    jours: number | null
}

/**
 * L'étude du scénario RETENU : c'est elle que l'en-tête et la preuve affichent,
 * donc c'est sa classe dimensionnante que les cartes et le conseil nomment.
 */
const getEtudeRetenue = computed(() =>
    props.retenu === "avec"
        ? (props.etudeAvec ?? props.etudeSans)
        : (props.etudeSans ?? props.etudeAvec)
)

/**
 * La classe dimensionnante, UNE seule vérité pour la page (revue du 25/09,
 * écarts 8 et 9) : la plage pilote servie par le moteur pour le scénario
 * retenu. Les cartes disaient « dénivelé HCB » en dur, et le conseil lisait
 * l'autre scénario (HPH quand l'en-tête disait HPB).
 */
const getPlage = computed(() => getClasseDimensionnante(getEtudeRetenue.value))

/**
 * L'arbitrage servi par le moteur pour cette paire (`outputs.arbitrage`,
 * courtage#251, DEV-2167) : conseil, nettes et phrase d'écart. `null` sur une
 * étude antérieure au lot : la page garde alors son repli.
 */
const getServi = computed(() =>
    lireArbitrageServi(props.etudeSans, props.etudeAvec)
)

/** Résumé d'un scénario : économie ferme + dénivelé/puissance avant→après (classe dimensionnante). */
function build(
    etude: OptimTurpeV2Etude | null,
    scenario: Scenario
): Card | null {
    if (!etude) return null
    const servis = getServi.value?.montants[scenario] ?? null
    const jours = lireChiffre(etude).periode.jours
    const unite = getUnitePuissance(etude)
    const isC5 = estC5(etude)
    const act =
        etude.actuelle?.structure_tarifaire?.puissance_souscrite_soutirage
    const best =
        etude.meilleure?.structure_tarifaire?.puissance_souscrite_soutirage
    let structure = ""
    if (isC5) {
        const avant = act?.puissance
        const apres = best?.puissance
        if (typeof avant === "number" && typeof apres === "number") {
            structure = t("puissance", {
                avant: numberFr.format(avant),
                apres: numberFr.format(apres),
                unite,
            })
        }
    } else {
        // S2 : toutes les puissances du scénario, poste par poste (un C4
        // n'a pas de poste de pointe), ou « Tous les postes » quand elles
        // passent toutes de la même valeur à la même valeur.
        const postes = PLAGE_ORDER.filter(
            (p) =>
                !(etude.compteur?.segment === "C4" && p === "PTE") &&
                typeof act?.denivele?.[p] === "number" &&
                typeof best?.denivele?.[p] === "number"
        )
        const paires = postes.map((p) => ({
            code: p,
            avant: act?.denivele?.[p] as number,
            apres: best?.denivele?.[p] as number,
        }))
        // Les postes qui passent d'une même valeur à une même valeur se
        // nomment ensemble (« postes de saison haute (HPH, HCH) »).
        const groupes = new Map<
            string,
            { codes: string[]; avant: number; apres: number }
        >()
        for (const x of paires) {
            const cle = `${x.avant}|${x.apres}`
            const g = groupes.get(cle)
            if (g) g.codes.push(x.code)
            else
                groupes.set(cle, {
                    codes: [x.code],
                    avant: x.avant,
                    apres: x.apres,
                })
        }
        structure = [...groupes.values()]
            .map((g) => {
                const nom = libellePostes(g.codes, paires.length)
                return t("groupe", {
                    postes: nom.charAt(0).toUpperCase() + nom.slice(1),
                    avant: numberFr.format(g.avant),
                    apres: numberFr.format(g.apres),
                    unite,
                })
            })
            .join(" · ")
    }
    // Les montants de l'arbitrage servi d'abord ; ceux de l'étude en repli.
    const fermeEur = servis?.ferme ?? getEconomieFermeEur(etude)
    const netteEur = servis?.nette ?? getEconomieNetteEur(etude)
    const depassements = etude.economie?.depassements_eur
    const depassementsEur =
        servis?.depassements ??
        (typeof depassements === "number" && Number.isFinite(depassements)
            ? Math.round(depassements)
            : null)
    return {
        // La nette quand elle est servie ; sinon la ferme, dite comme telle.
        principal:
            netteEur !== null
                ? (formatNombreSigne(netteEur) ?? t("fermeAbsente"))
                : fermeEur !== null
                  ? (formatNombreSigne(fermeEur) ?? t("fermeAbsente"))
                  : t("fermeAbsente"),
        // N3 : « € HT net par an » collé au nombre ; une période longue
        // passe à la ligne après son premier mot, sans déborder de la carte.
        suffixe: suiteColleeAuNombre(
            netteEur !== null || fermeEur === null
                ? t("netteUnite")
                : t("fermeUnite"),
            surLaPeriode(jours, lireChiffre(etude).periode)
        ),
        precision:
            netteEur === null && fermeEur !== null ? t("fermePrecision") : null,
        principalEur: netteEur ?? fermeEur,
        ferme: formatNombreSigne(fermeEur) ?? t("fermeAbsente"),
        // DEV-2217 : les dépassements assumés servis par le moteur (CMDPS de la
        // jambe « avec »), sinon ceux de l'étude.
        cmdps:
            (scenario === "avec"
                ? getServi.value?.depassementsAssumes
                : null) ?? getCmdpsEstimeeEur(etude),
        structure,
        fermeEur,
        depassementsEur,
        netteEur,
        jours,
    }
}

/**
 * La première phrase d'un texte servi : jusqu'au premier point final suivi
 * d'une majuscule ou de la fin. Un nombre décimal s'écrit avec une virgule,
 * il ne coupe donc pas la phrase.
 */
function premierePhrase(texte: string): string {
    const m = /^(.+?[.!?])(?=\s+[\p{Lu}«]|\s*$)/su.exec(texte.trim())
    return m ? m[1] : texte.trim()
}

const getSans = computed(() => build(props.etudeSans, "sans"))
const getAvec = computed(() => build(props.etudeAvec, "avec"))
/**
 * Scénario conseillé : celui que le moteur sert (règle v2 du 12/10/2026),
 * sinon la même règle recomposée par l'écran sur les montants servis, en
 * repli seulement (`conseilDeRepli`). Revue de nuit du 15/10/2026 (E6) : un
 * conseil que rien ne fonde (`conseilEtabli` nul) ne porte ni badge
 * « Recommandé » ni raison, comme la carte de tête et l'historique.
 */
const getRecommended = computed<Scenario | null>(() =>
    props.etudeAvec || props.etudeSans
        ? conseilEtabli(props.etudeSans, props.etudeAvec)
        : null
)

/**
 * La justification du conseil. Elle suit le scénario CONSEILLÉ (pour ne jamais
 * justifier « sans » par un pic isolé) et nomme la classe dimensionnante de la
 * page, celle de l'en-tête et des cartes.
 */
const getConseil = computed<string>(() => {
    const retenue = getEtudeRetenue.value
    if (!retenue) return ""
    const servi = getServi.value
    // Le motif composé par le moteur (DEV-2217), réduit à sa première phrase
    // (série UX du témoin, 2.3 point 6) : la suite redisait les cartes.
    if (servi?.motifServi) return premierePhrase(servi.motifServi)
    // Règle v2 du 12/10/2026 : la règle servie, sinon celle du repli. Les
    // règles 1 à 3 se disent par leurs montants, jamais par la forme du pic.
    const repli = servi?.conseil
        ? null
        : conseilDeRepli(props.etudeSans, props.etudeAvec)
    const regle = servi?.conseil ? servi.regle : (repli?.regle ?? null)
    // Une règle servie qui conseille l'AUTRE scénario que celui servi : la
    // page n'écrit aucune raison plutôt qu'une raison qui contredit le badge
    // « Conseillé » (revue du lot E).
    if (
        servi?.conseil &&
        regle !== null &&
        regle !== "forme-du-pic" &&
        SCENARIO_DE_LA_REGLE[regle] !== servi.conseil
    ) {
        return ""
    }
    if (regle === "aucun-depassement") return t("conseil.aucunDepassement")
    if (regle === "couvrir-moins-cher") return t("conseil.couvrirMoinsCher")
    if (regle === "gain-couvre-le-risque") {
        const netteSans = servi?.montants.sans.nette ?? null
        const netteAvec = servi?.montants.avec.nette ?? null
        const gain = repli
            ? repli.gainEur
            : netteSans !== null && netteAvec !== null
              ? netteAvec - netteSans
              : null
        const depassements = repli
            ? repli.depassementsEur
            : (servi?.depassementsAssumes ?? null)
        return gain !== null && depassements !== null
            ? t("conseil.gainCouvreLeRisque", {
                  gain: nombreEuros(gain),
                  depassements: nombreEurosDepassement(depassements),
              })
            : t("conseil.gainCouvreLeRisqueSansMontant")
    }
    const code = (servi?.conseil ? servi.plage : null) ?? getPlage.value ?? ""
    const plage = code ? libelleClasseHoraire(code) : ""
    if (getRecommended.value === "avec") return t("conseil.pic", { plage })
    const plageEtude = getPlage.value
    const driver = servi?.conseil
        ? servi.motif
        : plageEtude
          ? retenue.diagnostic?.plages?.[plageEtude]?.driver?.type
          : undefined
    if (driver === "saisonnier") return t("conseil.saisonnier", { plage })
    return t("conseil.normal", { plage })
})

/**
 * L'écart en euros entre les deux scénarios, lu sur les deux NETTES servies
 * (DEV-2167) : il s'affiche à côté du conseil (règle v2 du 12/10/2026) pour
 * que le client tranche en connaissance de cause.
 */
const getEcart = computed<string>(() => {
    // Maquette allégée : l'écart en un chiffre, sous les cartes. La phrase du
    // moteur n'est qu'un repli quand une nette manque.
    const sans = getSans.value?.netteEur ?? null
    const avec = getAvec.value?.netteEur ?? null
    if (sans === null || avec === null) return getServi.value?.phrase ?? ""
    const ecart = Math.abs(avec - sans)
    if (ecart === 0) return t("ecart.egal")
    return t("ecartCourt", {
        ecart: nombreEuros(ecart),
        periode: surLaPeriode(
            getSans.value?.jours ?? null,
            props.etudeSans ? lireChiffre(props.etudeSans).periode : null
        ),
    })
})

/**
 * La règle qui a décidé le conseil (servie, sinon le repli), et la forme de
 * la pointe quand c'est elle qui décide : la raison du badge « Recommandé »
 * (W50), en quelques mots.
 */
const getRaisonDuBadge = computed<string | null>(() => {
    const servi = getServi.value
    const repli = servi?.conseil
        ? null
        : conseilDeRepli(props.etudeSans, props.etudeAvec)
    const regle = servi?.conseil ? servi.regle : (repli?.regle ?? null)
    if (regle && regle !== "forme-du-pic") return phrase(t(`raison.${regle}`))
    const plage = getPlage.value
    const forme = servi?.conseil
        ? servi.motif
        : plage
          ? getEtudeRetenue.value?.diagnostic?.plages?.[plage]?.driver?.type
          : undefined
    return forme &&
        ["pic_isole", "comportement_normal", "saisonnier"].includes(forme)
        ? phrase(t(`raison.${forme}`))
        : null
})

/** « le gain vaut… » → « Le gain vaut… . » : une phrase sous l'étiquette. */
function phrase(texte: string): string {
    return `${texte.charAt(0).toUpperCase()}${texte.slice(1)}.`
}

/**
 * La réserve de la carte « Assumer les dépassements », en une ligne : une
 * seule durée (Q18), le montant estimé et, quand l'étude le sert, son écart
 * avec les dépassements du contrat actuel (S4). Sans dépassement établi
 * (règle servie ou durée servie nulle, jamais l'arrondi du montant), la carte
 * le dit (Q25).
 */
const getReserveAvec = computed<string>(() => {
    const cmdps = getAvec.value?.cmdps
    if (cmdps === null || cmdps === undefined) return t("cmdpsNonServie")
    const servi = getServi.value
    const regle = servi?.conseil
        ? servi.regle
        : (conseilDeRepli(props.etudeSans, props.etudeAvec)?.regle ?? null)
    if (aucunDepassementEtabli(regle, servi?.dureeDepassementH))
        return t("avec.aucunDepassement")
    // Décision du pilote (14/10/2026) : sous 0,50 €, « moins de 1 € HT »,
    // jamais « 0 € HT estimés ».
    const duree = libelleDureeHeures(getServi.value?.dureeDepassementH)
    const periode = surLaPeriode(
        getAvec.value?.jours ?? null,
        props.etudeAvec ? lireChiffre(props.etudeAvec).periode : null
    )
    return duree
        ? t("cmdpsAvec", {
              duree: `${duree}, `,
              value: nombreEurosDepassement(cmdps),
              periode,
          })
        : t("cmdpsAvecSansDuree", {
              value: nombreEurosDepassement(cmdps),
              periode,
          })
})

const getEnteteDetail = computed(() =>
    enteteDetailScenarios(
        getSans.value?.netteEur ?? null,
        getAvec.value?.netteEur ?? null
    )
)

/**
 * Un montant du détail, au nombre signé (N4 : l'unité est dans l'entête) ;
 * non servi : « — », jamais 0.
 */
function signe(valeur: number | null): string {
    return formatNombreSigne(valeur) ?? "—"
}

/** L'unité des montants du détail, « € HT net par an », dite une fois (N4). */
const getPeriodeDetail = computed(() =>
    surLaPeriode(
        getSans.value?.jours ?? getAvec.value?.jours ?? null,
        props.etudeSans
            ? lireChiffre(props.etudeSans).periode
            : props.etudeAvec
              ? lireChiffre(props.etudeAvec).periode
              : null
    )
)

/** Les deux cartes, dans l'ordre : même grille, mêmes rangs. */
const getCartes = computed(() =>
    (
        [
            ["sans", getSans.value],
            ["avec", getAvec.value],
        ] as const
    ).map(([cle, carte]) => ({
        cle,
        carte,
        nom: cle === "sans" ? t("sans.name") : t("avec.name"),
        reserve: cle === "sans" ? t("sans.cmdps") : getReserveAvec.value,
        reserveAlerte:
            cle === "avec" && Math.round(getAvec.value?.cmdps ?? 0) !== 0,
    }))
)

/**
 * Un C5 n'a pas de CMDPS (décision de Mathieu du 25/09, DEV-2164 cas 11) :
 * l'arbitrage « couvrir le pic ou l'assumer » n'y a pas d'objet.
 */
const getSansObjet = computed(
    () =>
        (props.etudeSans !== null && !cmdpsApplicable(props.etudeSans)) ||
        (props.etudeAvec !== null && !cmdpsApplicable(props.etudeAvec))
)

/**
 * Le pt de la carte résultat (story `BaseKPI`, `optimResultKpiCardPt`), la
 * valeur au niveau 1 de l'échelle, et le scénario affiché bordé.
 */
function ptDuScenario(cle: Scenario): BaseKpiPassThrough {
    return {
        root: [
            "!max-w-none w-full min-h-0 h-auto min-w-0 rounded-md border p-6 gap-4 bg-white shadow-none justify-start items-stretch mx-0 sm:max-w-none",
            props.retenu === cle
                ? "border-2 border-primary-500"
                : "border-surface-200",
        ],
        // Revue 2 (I07) : `normal-case!` l'emporte sur le `uppercase` du KPI
        // compact ; les noms se lisent en casse normale.
        title: "normal-case! text-xs font-medium text-primary-600 flex flex-wrap items-center justify-start gap-2 text-left",
        valueRow:
            "flex flex-col w-full min-w-0 flex-1 justify-start items-start text-left gap-1 min-h-[3rem]",
        value: "text-3xl font-bold",
        subtitle: "text-left text-sm leading-normal",
        footer: "text-left",
    }
}
</script>

<template>
    <!-- ================================ -->
    <!-- Z3 allégé (maquette du 07/10/2026) : le conseil en une phrase, les
         deux cartes côte à côte sur la même grille (titre et pastille,
         puissance qui change, montant net, une ligne de réserve, « Retenir ce
         scénario » sur la carte non retenue), la note d'écart, puis le détail
         des deux scénarios, replié. -->
    <!-- ================================ -->
    <div
        v-if="!getSansObjet"
        class="optim-turpe-v2-arbitrage flex min-w-0 flex-col gap-3"
    >
        <!-- Revue 2 (B1) : la phrase d'introduction prend la largeur de la
             rangée, deux lignes au plus à 1024 px. -->
        <p
            v-if="getRecommended && getConseil"
            class="text-sm text-surface-700"
            data-testid="optim-turpe-v2-arbitrage-conseil"
        >
            {{ getConseil }}
        </p>

        <!-- Maquette finale du 15/10/2026 : deux BaseKPI côte à côte (pt de
             la carte résultat de la story), le scénario affiché bordé. -->
        <div
            class="optim-turpe-v2-scenarios grid grid-cols-1 gap-4 md:grid-cols-2"
        >
            <BaseKPI
                v-for="{ cle, carte, nom, reserve, reserveAlerte } in getCartes"
                :key="cle"
                :label="nom"
                :value="carte?.principal ?? ''"
                :type="cle"
                :clickable="false"
                :pt="ptDuScenario(cle)"
                class="optim-turpe-v2-scenario"
                :data-retenu="retenu === cle ? '1' : '0'"
                :data-testid="`optim-turpe-v2-scenario-${cle}`"
            >
                <template #title>
                    <span
                        class="text-base font-semibold text-primary-900"
                        data-testid="optim-turpe-v2-scenario-nom"
                        >{{ nom }}</span
                    >
                    <BaseTag
                        v-if="getRecommended === cle"
                        severity="primary"
                        :value="t('conseille')"
                        data-testid="optim-turpe-v2-badge-recommande"
                    />
                    <BaseTag
                        v-else-if="getRecommended"
                        severity="secondary"
                        :value="t('optionChiffree')"
                        data-testid="optim-turpe-v2-badge-option"
                    />
                    <!-- Contrat v1.2 §6 2.3 (D2) : l'étiquette « Affiché » est
                         retirée, la bordure seule marque la tuile affichée. -->
                    <span
                        v-if="getRecommended === cle && getRaisonDuBadge"
                        class="w-full text-sm font-normal text-surface-700"
                        data-testid="optim-turpe-v2-raison-recommande"
                        >{{ getRaisonDuBadge }}</span
                    >
                </template>
                <template #value>
                    <span class="text-sm text-surface-700">{{
                        carte?.structure
                    }}</span>
                    <!-- N3 (19/10/2026) : le nombre et son unité sur la même
                         ligne, « −42 € HT net par an », signe et couleur
                         gardés ; jamais l'unité sur une ligne à part. Texte
                         courant : les espaces insécables de la suite la
                         collent au nombre, une période longue passe à la
                         ligne au lieu de déborder de la carte à 375 px. -->
                    <span
                        class="block"
                        :class="
                            classeMontant(
                                carte?.principalEur ?? null,
                                'text-primary-900'
                            )
                        "
                        data-testid="optim-turpe-v2-scenario-kpi"
                    >
                        <span
                            class="text-3xl font-bold"
                            data-testid="optim-turpe-v2-scenario-montant"
                            >{{ carte?.principal }}</span
                        ><span
                            class="text-lg font-semibold"
                            data-testid="optim-turpe-v2-scenario-unite"
                            >{{ carte?.suffixe ?? "" }}</span
                        >
                    </span>
                    <span
                        v-if="carte?.precision"
                        class="text-sm text-surface-500"
                        >{{ carte.precision }}</span
                    >
                </template>
                <template #subtitle>
                    <span
                        class="text-sm"
                        :class="
                            reserveAlerte
                                ? 'text-[var(--p-orange-700)]'
                                : 'text-surface-700'
                        "
                        :data-testid="
                            cle === 'avec'
                                ? 'optim-turpe-v2-arbitrage-duree'
                                : undefined
                        "
                    >
                        {{ reserve }}
                    </span>
                </template>
                <template v-if="retenu !== cle" #footer>
                    <BaseButton
                        v-bind="NIVEAU_2"
                        :label="t('retenir')"
                        :data-testid="`optim-turpe-v2-retenir-${cle}`"
                        @click="emit('select', cle)"
                    />
                </template>
            </BaseKPI>
        </div>

        <p
            class="text-sm text-surface-700"
            data-testid="optim-turpe-v2-arbitrage-ecart"
        >
            {{ [getEcart, t("footnote")].filter(Boolean).join(" ") }}
        </p>

        <OptimTurpeV2Section
            id="acc-detail-scenarios"
            :entete="getEnteteDetail"
            :rang="2"
            :ouvert="detailOuvert"
            @update:ouvert="emit('detail', $event)"
        >
            <div class="overflow-x-auto">
                <table class="w-full min-w-[34rem] text-sm">
                    <thead class="text-xs text-primary-500">
                        <tr>
                            <th class="py-1 pr-3 text-left font-normal">
                                {{ t("detail.scenario") }}
                            </th>
                            <th class="py-1 pr-3 text-right font-normal">
                                {{
                                    t("detail.ferme", {
                                        periode: getPeriodeDetail,
                                    })
                                }}
                            </th>
                            <th class="py-1 pr-3 text-right font-normal">
                                {{
                                    t("detail.depassements", {
                                        periode: getPeriodeDetail,
                                    })
                                }}
                            </th>
                            <th class="py-1 text-right font-normal">
                                {{
                                    t("detail.nette", {
                                        periode: getPeriodeDetail,
                                    })
                                }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="{ cle, carte, nom } in getCartes"
                            :key="cle"
                            class="border-t border-surface-100"
                        >
                            <th class="py-1 pr-3 text-left font-normal">
                                {{ nom }}
                            </th>
                            <td
                                class="py-1 pr-3 text-right"
                                :class="classeMontant(carte?.fermeEur ?? null)"
                                :data-testid="`optim-turpe-v2-scenario-ferme-${cle}`"
                            >
                                {{ signe(carte?.fermeEur ?? null) }}
                            </td>
                            <td
                                class="py-1 pr-3 text-right"
                                :class="
                                    classeMontant(
                                        carte?.depassementsEur ?? null
                                    )
                                "
                                :data-testid="`optim-turpe-v2-scenario-depassements-${cle}`"
                            >
                                {{
                                    carte?.depassementsEur === 0
                                        ? t("aucunDepassement")
                                        : signe(carte?.depassementsEur ?? null)
                                }}
                            </td>
                            <td
                                class="py-1 text-right font-semibold"
                                :class="classeMontant(carte?.netteEur ?? null)"
                                :data-testid="`optim-turpe-v2-scenario-nette-${cle}`"
                            >
                                {{ signe(carte?.netteEur ?? null) }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </OptimTurpeV2Section>
    </div>
</template>
